import { createAction, handleActions } from '@gef-ui/utils/fsaActions';
import assoc from "ramda/es/assoc";
import assocPath from "ramda/es/assocPath";
import compose from "ramda/es/compose";
import dec from "ramda/es/dec";
import equals from "ramda/es/equals";
import filter from "ramda/es/filter";
import identity from "ramda/es/identity";
import init from "ramda/es/init";
import last from "ramda/es/last";
import lensIndex from "ramda/es/lensIndex";
import map from "ramda/es/map";
import mapObjIndexed from "ramda/es/mapObjIndexed";
import mergeRight from "ramda/es/mergeRight";
import omit from "ramda/es/omit";
import over from "ramda/es/over";
import reject from "ramda/es/reject";
import transduce from "ramda/es/transduce";
import values from "ramda/es/values";
import { merge } from 'rxjs';
import { filter as rxFilter, map as rxMap, scan, shareReplay, withLatestFrom } from 'rxjs/operators';
import { createFieldRecord, getArrayDeepPath, getArrayNames, metaFieldIndices, metaFieldName, metaFieldRecord, metaSection, payloadDisabled, payloadInitiallyDirty, payloadKeepPristine } from '../../../utils/data';
import { fieldType } from '../../../validations/constants';
import { actionNames, initFormFieldState } from '../../transfer/control';
import { actionNames as transmissionActions } from '../../transfer/transmission';
import { bufferControl, filterByActionType, skipWhenStateEqual } from '../../utils';
const BUFFERED_INPUT = 'BUFFERED_INPUT';
const bufferedInput = createAction(BUFFERED_INPUT, identity);
const makeFieldDirty = (state, action)=>{
    const fieldRecord = metaFieldRecord(action);
    if (state[fieldRecord] && (!state[fieldRecord].pristine || payloadKeepPristine(action))) {
        return state;
    }
    return assocPath([
        fieldRecord,
        'pristine'
    ], false, state);
};
const setAsyncValidationState = (state, { payload })=>mapObjIndexed((field, fieldRecord)=>({
            ...field,
            asyncValidationInProgress: payload[fieldRecord]?.value ?? field.asyncValidationInProgress
        }), state);
const updateValidationResult = (state, { meta })=>{
    const newState = {
        ...state
    };
    let updated = false;
    meta.pristine.forEach((fieldRecord)=>{
        if (newState[fieldRecord]) {
            updated = true;
            newState[fieldRecord].pristine = true;
        }
    });
    if (updated) {
        return newState;
    }
    return state;
};
const shiftFieldStates = (state, { payload })=>({
        ...state,
        ...transduce(compose(filter((record)=>equals(record.arrayDeepPath, payload.deepPath) && last(record.fieldIndices) > payload.index), map((record)=>({
                ...record,
                fieldIndices: over(lensIndex(record.fieldIndices.length - 1), dec, record.fieldIndices)
            }))), (acc, record)=>{
            // mutate accumulator for performance.
            acc[createFieldRecord(record.fieldName, record.fieldIndices)] = record;
            return acc;
        }, {}, values(state))
    });
const clearFieldStates = (state, { payload })=>reject((record)=>equals(record.arrayDeepPath, payload), state);
const makeFieldDisabled = (state, action)=>{
    const fieldRecord = metaFieldRecord(action);
    // note: ignore disabled action when field record is not initialized yet.
    if (!state?.[fieldRecord] || state?.[fieldRecord]?.disabled === payloadDisabled(action)) {
        return state;
    }
    return assocPath([
        fieldRecord,
        'disabled'
    ], payloadDisabled(action), state);
};
const mapInitState = (action, formDescriptor)=>{
    const fieldName = metaFieldName(action);
    const fieldIndices = metaFieldIndices(action);
    const { type, array } = formDescriptor[metaFieldName(action)];
    let record = {
        asyncValidationInProgress: false,
        pristine: !payloadInitiallyDirty(action),
        initiallyDirty: payloadInitiallyDirty(action),
        fieldIndices: metaFieldIndices(action),
        fieldName,
        disabled: payloadDisabled(action),
        section: metaSection(action)
    };
    if (type === fieldType.FIELD_ARRAY) {
        record = {
            ...record,
            arrayDeepPath: getArrayDeepPath(formDescriptor, array, init(fieldIndices)),
            arrayPlacementPath: getArrayNames(formDescriptor, array, init(fieldIndices))
        };
    }
    return {
        [metaFieldRecord(action)]: record
    };
};
const registerUnregister = (state, { payload: { bufferedActions, formDescriptor } })=>{
    let newState = {
        ...state
    };
    bufferedActions.forEach((action)=>{
        if (action.type === actionNames.REGISTER) {
            newState = mergeRight(newState, mapInitState(action, formDescriptor));
        } else {
            newState = omit([
                metaFieldRecord(action)
            ], newState);
        }
    });
    return newState;
};
const scanReducer = (reducerHandler)=>handleActions({
        [actionNames.INIT_FORM_FIELD_STATE]: (_state, { payload })=>payload,
        [actionNames.RESET_FORM]: map((fieldState)=>assoc('pristine', !fieldState.initiallyDirty, fieldState)),
        BUFFERED_INPUT: registerUnregister,
        [actionNames.SET_FIELD_DISABLED]: makeFieldDisabled,
        [actionNames.REMOVE_FROM_LIST]: shiftFieldStates,
        [actionNames.CLEAR_LIST]: clearFieldStates,
        [actionNames.SET_VALUE]: makeFieldDirty,
        [actionNames.BLUR]: makeFieldDirty,
        [transmissionActions.UPDATE_ASYNC_VALIDATION_STATE]: setAsyncValidationState,
        [transmissionActions.UPDATE_VALIDATION_RESULT]: updateValidationResult,
        ...reducerHandler
    }, {});
export default (({ control$, formDescriptor$ }, { createInputs = [], reducerHandler = {} } = {}, initState = {})=>{
    const extensionInputs$ = map((ext)=>ext({
            control$
        }), createInputs);
    const formFieldState$ = merge(control$.pipe(filterByActionType(actionNames.INIT_FORM_FIELD_STATE, actionNames.SET_VALUE, actionNames.BLUR, actionNames.REMOVE_FROM_LIST, actionNames.CLEAR_LIST, actionNames.SET_FIELD_DISABLED, transmissionActions.UPDATE_ASYNC_VALIDATION_STATE)), control$.pipe(filterByActionType(control$, actionNames.RESET_FORM), rxFilter(({ meta })=>meta.pristine)), control$.pipe(filterByActionType(control$, transmissionActions.UPDATE_VALIDATION_RESULT), rxFilter(({ meta })=>meta?.pristine?.length > 0)), control$.pipe(bufferControl(control$, filterByActionType(actionNames.UNREGISTER, actionNames.REGISTER)), withLatestFrom(formDescriptor$), rxMap(([bufferedActions, formDescriptor])=>bufferedInput({
            bufferedActions,
            formDescriptor
        }))), ...extensionInputs$).pipe(scan(scanReducer(reducerHandler), {}), skipWhenStateEqual(), shareReplay(1));
    formFieldState$.subscribe();
    control$.next(initFormFieldState({}));
    control$.next(initFormFieldState(initState));
    return formFieldState$;
});
